import { Children, useEffect, useRef, useState, type ReactNode } from 'react';

export const REPORT_PAGE_WIDTH = 794;
export const REPORT_PAGE_HEIGHT = 1123;

const PAGE_GAP = 32;

interface ReportPreviewLayoutProps {
    children: ReactNode;
    loading?: boolean;
    loadingMessage?: string;
}

/**
 * Scaling/snap-scroll container for A4 report pages.
 *
 * Each direct child is treated as one page and wrapped in a
 * snap-start div with dynamic scaling to fit the available width.
 *
 * The scroll container is always mounted so the ResizeObserver can
 * track width and compute the correct scale factor before pages render.
 */
export function ReportPreviewLayout({
    children,
    loading = false,
    loadingMessage = 'Loading report data…',
}: ReportPreviewLayoutProps) {
    const scrollRef = useRef<HTMLDivElement>(null);
    const [scale, setScale] = useState(1);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        const observer = new ResizeObserver(([entry]) => {
            const available = entry.contentRect.width - PAGE_GAP * 2;
            setScale(Math.min(1, available / REPORT_PAGE_WIDTH));
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    const wrapperHeight = REPORT_PAGE_HEIGHT * scale + PAGE_GAP * 2;

    const pageStyle: React.CSSProperties = {
        position: 'absolute',
        top: PAGE_GAP,
        left: '50%',
        transform: `translateX(-50%) scale(${scale})`,
        transformOrigin: 'top center',
    };

    const pages = loading ? [] : Children.toArray(children).flat();

    return (
        <div ref={scrollRef} className="h-full overflow-y-auto snap-y snap-mandatory bg-neutral-200">
            {loading ? (
                <div className="flex h-full items-center justify-center text-muted-foreground">
                    {loadingMessage}
                </div>
            ) : (
                pages.map((child, index) => (
                    <div key={index} className="snap-start relative" style={{ height: wrapperHeight }}>
                        <div style={pageStyle}>{child}</div>
                    </div>
                ))
            )}
        </div>
    );
}
